import React, { useState } from 'react';

// Material UI
import { Box, Button, Typography } from '@mui/material';
import { Card, CardHeader, CardContent, CardActions } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

// Components
import { AdminPage } from 'components/ui/layout';
import { ErrorState, LoadingState } from 'components/ui/states';
import { SearchBar, TextField } from 'components/ui/inputs';
import { ActionButton, SubmitButton, CancelButton, DeleteButton } from 'components/ui/buttons';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import { formatPriceFromCents } from 'utility/numbers';
import { useNumberInput } from 'hooks/forms';
import { useNotification } from 'context/notification';

export default function ConnectLookup() {
    // Connect Lookup State
    const [connect, setConnect] = useState({
        account: null,
        loading: false,
        error: '',
    });

    // Transfer State
    const [transferDialog, setTransferDialog] = useState({
        open: false,
        loading: false,
        value: '',
    });

    // Delete State
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        loading: false,
    });

    const { notify } = useNotification();

    let transferEnabled = false;
    let deleteEnabled = false;

    if (connect.account) {
        if (connect.account.balance.available > 0) {
            transferEnabled = true;
        }
        if (
            connect.account.host_name === null &&
            connect.account.balance.available === 0 &&
            connect.account.balance.pending === 0
        ) {
            deleteEnabled = true;
        }
    }

    const handleSubmit = async (value) => {
        setConnect((c) => ({ ...c, account: null, loading: true, error: '' }));
        try {
            const response = await fetchExpresso(`/apiv2/connect/${value}`);
            if (response.status === 200) {
                const data = await response.json();
                setConnect((c) => ({
                    ...c,
                    account: {
                        host_name: data.host_name,
                        connect_id: data.connect_id,
                        charges_enabled: data.charges_enabled,
                        payouts_enabled: data.payouts_enabled,
                        onboarding_complete: data.details_submitted,
                        balance: {
                            available: data.balance.available,
                            pending: data.balance.pending,
                        },
                    },
                    loading: false,
                }));
            } else {
                setConnect((c) => ({
                    ...c,
                    loading: false,
                    error: 'Unable to find connect account',
                }));
            }
        } catch (err) {
            setConnect((c) => ({ ...c, loading: false, error: 'An error occurred' }));
        }
    };

    const handleTransferBalance = (amount) => {
        setTransferDialog((t) => ({ ...t, loading: true }));

        fetchExpresso(`/apiv2/connect/${connect.account.connect_id}/transfer`, {
            method: 'PUT',
            body: { amount },
        })
            .then((result) => {
                if (result.status === 200) {
                    notify.success('Balance transferred successfully');
                    setTransferDialog((t) => ({ ...t, open: false, loading: false }));
                    handleSubmit(connect.account.connect_id); // reload the connect balance
                } else {
                    throw new Error(`Got ${result.status} response code`);
                }
            })
            .catch((error) => {
                notify.error(String(error));
                setTransferDialog((t) => ({ ...t, loading: false }));
            });
    };

    const handleDeleteAccount = () => {
        setDeleteDialog((d) => ({ ...d, loading: true }));

        fetchExpresso(`/apiv2/connect/${connect.account.connect_id}`, { method: 'DELETE' })
            .then((result) => {
                if (result.status === 200) {
                    notify.success('Account deleted successfully');
                    setDeleteDialog((d) => ({ ...d, open: false, loading: false }));
                    setConnect((c) => ({ ...c, account: null }));
                } else if (result.status === 404) {
                    throw new Error(
                        'Unable to find connect account. It may have already been deleted.',
                    );
                } else {
                    throw new Error(`Got ${result.status} response code`);
                }
            })
            .catch((error) => {
                notify.error(String(error));
                setDeleteDialog((d) => ({ ...d, loading: false }));
            });
    };

    return (
        <AdminPage>
            <SearchHeader onSearch={handleSubmit} />

            {connect.error && <ErrorState message={connect.error} />}

            {connect.loading && <LoadingState />}

            {connect.loading === false && connect.account && (
                <Card>
                    <CardHeader
                        title={connect.account.connect_id}
                        subheader={connect.account.host_name || undefined}
                    />
                    <CardContent>
                        <Typography>
                            Pending Balance: {formatPriceFromCents(connect.account.balance.pending)}
                        </Typography>
                        <Typography>
                            Available Balance:{' '}
                            {formatPriceFromCents(connect.account.balance.available)}
                        </Typography>
                    </CardContent>
                    <CardActions>
                        <ActionButton
                            disabled={!transferEnabled}
                            onClick={() => setTransferDialog((t) => ({ ...t, open: true }))}
                        >
                            Transfer Balance
                        </ActionButton>
                        <DeleteButton
                            disabled={!deleteEnabled}
                            onClick={() => setDeleteDialog((t) => ({ ...t, open: true }))}
                        >
                            Delete Account
                        </DeleteButton>
                    </CardActions>

                    <TransferDialog
                        open={transferDialog.open}
                        loading={transferDialog.loading}
                        available={connect.account.balance.available}
                        pending={connect.account.balance.pending}
                        onClose={() => setTransferDialog((t) => ({ ...t, open: false }))}
                        onTransfer={handleTransferBalance}
                    />

                    <DeleteDialog
                        open={deleteDialog.open}
                        loading={deleteDialog.loading}
                        host={connect.account.host_name}
                        onClose={() => setDeleteDialog((t) => ({ ...t, open: false }))}
                        onDelete={handleDeleteAccount}
                    />
                </Card>
            )}
        </AdminPage>
    );
}

function SearchHeader(props) {
    const [connectId, setConnectId] = useState('');

    const handleConnectIdChange = (value) => {
        // Connect IDs only contain letters, numbers and underscores
        // Ex: acct_1QpDSB2nA8lBQmkc
        const sanitizedValue = value.replace(/[^a-zA-Z0-9_]/g, '');
        setConnectId(sanitizedValue);
    };

    // Submit if enter key is hit
    function onKeyPress(e) {
        if (e.key === 'Enter') {
            props.onSearch(connectId);
        }
    }

    return (
        <Box mb={4} display="flex" maxWidth={500}>
            <SearchBar
                autoFocus
                placeholder="Connect ID"
                onKeyPress={onKeyPress}
                value={connectId}
                onChange={handleConnectIdChange}
            />
            <Box ml={2}>
                <Button
                    variant="contained"
                    disabled={connectId.length === 0}
                    onClick={() => props.onSearch(connectId)}
                >
                    Search
                </Button>
            </Box>
        </Box>
    );
}

function TransferDialog(props) {
    const { open, loading, available, pending, onClose, onTransfer } = props;
    const transfer = useNumberInput({ decimalPlaces: 2 });

    const handleClose = () => {
        transfer.setValue('');
        onClose();
    };

    const handleSubmit = () => {
        const value = Number(transfer.value);

        if (isNaN(value) || value === 0) {
            transfer.setError('Please enter a valid amount');
            return;
        }

        const cents = Math.round(value * 100);

        if (cents > available) {
            transfer.setError('Amount is greater than the available balance');
            return;
        }

        onTransfer(cents);
    };

    return (
        <Dialog fullWidth open={open} onClose={handleClose}>
            <DialogTitle>Transfer Balance</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography>
                        The amount entered will be transfered from the connect account to the
                        FrontDoor+ Platform account
                    </Typography>
                </Box>
                <Box mb={2} display={'flex'} flexDirection={'column'}>
                    <Typography variant="body2">
                        Pending Balance: {formatPriceFromCents(pending)}
                    </Typography>
                    <Typography variant="body2">
                        Available Balance: {formatPriceFromCents(available)}
                    </Typography>
                </Box>
                <TextField {...transfer.formProps} />
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading} onClick={handleClose}>
                    Cancel
                </CancelButton>
                <SubmitButton disabled={loading} onClick={handleSubmit}>
                    Transfer
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function DeleteDialog(props) {
    const { open, loading, onClose, onDelete } = props;

    return (
        <Dialog fullWidth open={open} onClose={onClose}>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogContent>
                <Typography>
                    The connected account will be deleted. This cannot be undone.
                </Typography>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading} onClick={onClose}>
                    Cancel
                </CancelButton>
                <DeleteButton disabled={loading} onClick={onDelete}>
                    Delete
                </DeleteButton>
            </DialogActions>
        </Dialog>
    );
}